import { yupResolver } from '@hookform/resolvers/yup';
import { sendGTMEvent } from '@next/third-parties/google';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { CountryData } from 'react-phone-input-2';
import * as yup from 'yup';

import { AuthLoadingOverlay, SsoProviders } from '@/components/modals/sso';
import { AlreadyLoggedInNotice } from '@/components/trial/already-logged-in-notice';
import type { AlertStateOrEmpty } from '@/components/ui/alert';
import { Alert } from '@/components/ui/alert';
import CaptchaProvider from '@/components/ui/captcha-provider';
import { CAPTCHA_BYPASS_TOKEN } from '@/constants/captcha';
import type { CreateUserTrialParams } from '@/context/types';
import { CountryCodeEnum } from '@/enums/country-code-enum';
import { useAuth } from '@/hooks/use-auth';
import { useCountryCode } from '@/hooks/use-country-code';
import { apiErrorMessageKey, TRIAL_STATUS_KEYS } from '@/utils/api-error-i18n';
import { useCountryNamesPhone } from '@/utils/country-names-phone';
import { personNameSchema } from '@/utils/person-name';
import { getUtmData } from '@/utils/user-tracking-data';

import { NAMESPACE, NewTabLink } from './ui';

// The only field that needs the 44 KB phone stylesheet, and only for Russian visitors: kept off the
// critical path the way src/components/ui/phone-field.tsx asks.
const PhoneInput = dynamic(() => import('@/components/ui/phone-field'), { ssr: false });

// The design's registration card ("Teste grátis sem cartão") wired to the same trial signup as the shared
// CreateAccount form: same fields and schema, captcha, SSO with the trial context, the already-logged-in
// notice, the Alert for API errors, `useAuth().createTrial` with `custom` (= trial CONFIG id) +
// `trialPageId` + UTM (the partner click id is merged by the auth context), then login, the GTM
// `createAccount` event and the /thanks-for-trial-no-card redirect. It replaces the design's
// `np:trial-submit` hook, which only painted "Pronto para integração". The same instance moves between
// the hero and the trial dialog (see ../index.tsx), so what the visitor typed survives either way.

interface SignupFormProps {
  daysTrial: number;
  trialConfigId: number | null;
  trialPageId: number | null;
  isDisabledButton?: boolean;
}

interface FormValues {
  name: string;
  email: string;
  repeatEmail: string;
  password: string;
  passwordRepeat: string;
  recaptcha: string;
  telegram?: string | null;
}

type FieldName = 'name' | 'email' | 'repeatEmail' | 'password' | 'passwordRepeat';
type CaptchaState = 'loading' | 'ready' | 'error' | 'mounted';

const FIELD_ORDER: FieldName[] = ['name', 'email', 'repeatEmail', 'password', 'passwordRepeat'];
const CAPTCHA_WAIT = 8000;

const OkIcon = () => (
  <svg className='np-field__ok' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
    <circle cx='12' cy='12' r='9' />
    <path d='m8.4 12.3 2.4 2.4 4.8-5' />
  </svg>
);

const ErrorIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
    <circle cx='12' cy='12' r='9' />
    <path d='M12 7.8v4.9' />
    <path d='M12 16.2h.01' />
  </svg>
);

const EyeIcons = () => (
  <>
    <svg className='ico-on' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
      <path d='M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z' />
      <circle cx='12' cy='12' r='3' />
    </svg>
    <svg className='ico-off' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
      <path d='M3 3l18 18' />
      <path d='M10.6 6.1A9.8 9.8 0 0 1 12 6c6 0 9.5 6 9.5 6a16.6 16.6 0 0 1-3.2 3.7' />
      <path d='M6.6 6.7A16.4 16.4 0 0 0 2.5 12S6 18 12 18a9.6 9.6 0 0 0 4.2-.9' />
      <path d='M9.9 9.9a3 3 0 0 0 4.2 4.2' />
    </svg>
  </>
);

/** The arrow is bound to the last words of the label (one nowrap unit), however the label wraps. */
const SubmitEnd = ({ children }: { children?: ReactNode }) => (
  <span className='np-form__submit-end'>
    {children}
    <svg className='np-form__submit-arrow' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
      <path d='M5 12h14' />
      <path d='m13 6 6 6-6 6' />
    </svg>
  </span>
);

export function SignupForm({ trialConfigId, trialPageId, isDisabledButton = false }: SignupFormProps) {
  const { t } = useTranslation([NAMESPACE, 'common']);
  const router = useRouter();
  const auth = useAuth();
  const countryCodeUser = useCountryCode();
  // useCountryNamesPhone calls useTranslation, so it runs here with the other hooks: called inside the
  // Russian-only JSX, it added a hook when the country resolved after mount (React #310).
  const phoneLocalization = useCountryNamesPhone();
  const [isLoading, setIsLoading] = useState(false);
  const [visible, setVisible] = useState<Record<'password' | 'passwordRepeat', boolean>>({ password: false, passwordRepeat: false });
  const [popUp, setPopUp] = useState<{ show: boolean; message: string; state: AlertStateOrEmpty }>({ show: false, message: '', state: '' });
  const [isSsoAuthenticating, setIsSsoAuthenticating] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [captchaState, setCaptchaState] = useState<CaptchaState>('loading');
  const captchaResetRef = useRef<(() => void) | null>(null);
  const captchaSlotRef = useRef<HTMLDivElement>(null);
  const tf = (key: string, options?: Record<string, unknown>) => String(t(`${NAMESPACE}:form.${key}`, options));

  const schema = yup.object().shape({
    name: personNameSchema(tf('errors.required'), tf('errors.invalid_name')),
    email: yup.string().required(tf('errors.required')).email(tf('errors.email')).max(255),
    repeatEmail: yup
      .string()
      .required(tf('errors.required'))
      .email(tf('errors.email'))
      .max(255)
      .oneOf([yup.ref('email')], tf('errors.email_match')),
    password: yup.string().required(tf('errors.required')).min(6, tf('errors.password_min')),
    passwordRepeat: yup
      .string()
      .required(tf('errors.required'))
      .min(6, tf('errors.password_min'))
      .oneOf([yup.ref('password')], tf('errors.password_match')),
    recaptcha: yup.string().required(tf('errors.captcha')),
    telegram: yup.string().nullable().notRequired(),
  });

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    reset,
    watch,
    formState: { errors, touchedFields },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { name: '', email: '', repeatEmail: '', password: '', passwordRepeat: '', recaptcha: '', telegram: '' },
    mode: 'onBlur',
  });
  const values = watch();

  const resetCaptcha = useCallback(() => {
    setValue('recaptcha', '');
    captchaResetRef.current?.();
  }, [setValue]);

  const handleCaptchaChange = useCallback(
    (token: string | null) => {
      setValue('recaptcha', token ?? '');
      clearErrors('recaptcha');
      // In the countries where the captcha is bypassed no widget ever mounts: the row settles as "automatic".
      if (token === CAPTCHA_BYPASS_TOKEN) setCaptchaState('ready');
    },
    [setValue, clearErrors]
  );
  const handleCaptchaExpired = useCallback(() => {
    setValue('recaptcha', '');
    setError('recaptcha', { type: 'required', message: String(t(`${NAMESPACE}:form.errors.captcha`)) });
  }, [setValue, setError, t]);

  // The design's settled row stands in for the widget until it mounts (or for good where it never does);
  // a widget that has not arrived after CAPTCHA_WAIT turns it into the failure state with a retry.
  useEffect(() => {
    const slot = captchaSlotRef.current;
    if (!slot) return undefined;
    const sync = () => {
      if (slot.childElementCount > 0) setCaptchaState('mounted');
    };
    sync();
    const observer = typeof MutationObserver === 'function' ? new MutationObserver(sync) : null;
    observer?.observe(slot, { childList: true, subtree: true });
    return () => observer?.disconnect();
  }, []);
  useEffect(() => {
    if (captchaState !== 'loading') return undefined;
    const timer = window.setTimeout(() => setCaptchaState((current) => (current === 'loading' ? 'error' : current)), CAPTCHA_WAIT);
    return () => window.clearTimeout(timer);
  }, [captchaState]);

  const handleSsoStart = useCallback(() => setIsSsoAuthenticating(true), []);
  const handleSsoSuccess = useCallback(() => setIsSsoAuthenticating(false), []);
  const handleSsoError = useCallback((message: string) => {
    setPopUp({ show: true, message, state: 'error' });
    setIsSsoAuthenticating(false);
  }, []);

  const onSubmit = async (data: FormValues) => {
    if (isDisabledButton || isLoading) return;
    setShowSummary(false);
    setIsLoading(true);
    const body: CreateUserTrialParams = {
      email: data.email,
      password: data.password,
      password_repeat: data.passwordRepeat,
      recaptcha: data.recaptcha,
      ref: null,
      name: data.name,
      lang: router.locale ?? 'en',
      ...getUtmData(),
      // `custom` IS the trial config id — the endpoint's name for it. Both ids have to reach the API or
      // the account is created with no trial at all, and it still answers 201.
      custom: trialConfigId,
      trialPageId,
    };
    if (data.telegram) body.telegram = data.telegram;

    try {
      await auth.createTrial(
        body,
        (error) => {
          resetCaptcha();
          setPopUp({ show: true, message: String(t(apiErrorMessageKey(error, { fallbackKey: 'common:login.mensage_error_create_user_generic', statusKeys: TRIAL_STATUS_KEYS }))), state: 'error' });
          setIsLoading(false);
        },
        () => {
          auth.login({ login: data.email, password: data.password, recaptcha: data.recaptcha });
          sendGTMEvent({ event: 'createAccount' });
          reset();
          sessionStorage.setItem('customTrialApproved', 'true');
          router.push('/thanks-for-trial-no-card');
          setIsLoading(false);
        },
        false
      );
    } catch {
      resetCaptcha();
      setPopUp({ show: true, message: String(t('common:login.mensage_error_create_user_generic')), state: 'error' });
      setIsLoading(false);
    }
  };

  const errorCount = FIELD_ORDER.filter((field) => errors[field]).length + (errors.recaptcha ? 1 : 0);
  useEffect(() => {
    if (showSummary && errorCount === 0) setShowSummary(false);
  }, [showSummary, errorCount]);

  // A logged-in visitor gets the notice that can start the SAME trial from the existing account.
  if (auth.user) {
    return (
      <div className='np-form' id='np-form'>
        <AlreadyLoggedInNotice trialConfigId={trialConfigId} trialPageId={trialPageId} />
      </div>
    );
  }

  const fieldState = (field: FieldName) => {
    const error = errors[field]?.message;
    const valid = !error && !!touchedFields[field] && String(values[field] ?? '').trim().length > 0;
    return { error, valid };
  };

  const renderField = (field: FieldName, inputProps: { id: string; type: string; autoComplete: string; inputMode?: 'email'; label: string; placeholder: string; hint?: string; toggle?: { show: string; hide: string } }) => {
    const { error, valid } = fieldState(field);
    const msgId = `${inputProps.id}-msg`;
    const hintId = inputProps.hint ? `${inputProps.id}-hint` : undefined;
    const isPassword = !!inputProps.toggle;
    const toggleKey = field === 'passwordRepeat' ? 'passwordRepeat' : 'password';
    const shown = isPassword && visible[toggleKey];
    const label: ReactNode = (
      <label className='np-field__label' htmlFor={inputProps.id}>
        {inputProps.label}
        <OkIcon />
      </label>
    );
    return (
      <div className={classNames('np-field', isPassword && 'np-field--password', error && 'np-field--invalid', valid && 'np-field--valid')}>
        {inputProps.hint ? (
          <div className='np-field__top'>
            {label}
            <span className='np-field__hint' id={hintId}>
              {inputProps.hint}
            </span>
          </div>
        ) : (
          label
        )}
        <div className='np-field__control'>
          <input className='np-field__input' id={inputProps.id} type={shown ? 'text' : inputProps.type} inputMode={inputProps.inputMode} maxLength={255} autoComplete={inputProps.autoComplete} dir={inputProps.autoComplete === 'name' ? 'auto' : undefined} autoCapitalize={inputProps.inputMode ? 'off' : undefined} spellCheck={inputProps.inputMode ? false : undefined} placeholder={inputProps.placeholder} aria-required='true' aria-invalid={error ? 'true' : undefined} aria-describedby={[hintId, msgId].filter(Boolean).join(' ')} data-lpignore='true' {...register(field)} />
          {inputProps.toggle && (
            <button className='np-field__toggle' type='button' aria-controls={inputProps.id} data-visible={shown ? 'true' : 'false'} aria-label={shown ? inputProps.toggle.hide : inputProps.toggle.show} onClick={() => setVisible((previous) => ({ ...previous, [toggleKey]: !previous[toggleKey] }))}>
              <EyeIcons />
            </button>
          )}
        </div>
        <span className={classNames('np-field__msg', error && 'is-error')} id={msgId}>
          {error}
        </span>
      </div>
    );
  };

  const summaryText = errorCount === 1 ? tf('errors.fix_single') : tf('errors.fix_several', { count: errorCount });
  const summary = showSummary && errorCount > 0 ? summaryText : '';
  const captchaFailed = captchaState === 'error';

  return (
    <form className='np-form gtm-sign-up' id='np-form' noValidate autoComplete='on' aria-labelledby='np-form-title' onSubmit={handleSubmit(onSubmit, () => setShowSummary(true))}>
      <AuthLoadingOverlay isActive={isSsoAuthenticating} />
      <div className='np-form__head'>
        <h2 className='np-form__title' id='np-form-title'>
          <Trans t={t} ns={NAMESPACE} i18nKey='form.title' components={{ hl: <span className='np-form__hl' /> }} />
        </h2>
        <p className='np-form__sub'>
          {tf('sub_card')} <span className='dot' aria-hidden='true' /> {tf('sub_access')}
        </p>
      </div>

      <p className='np-form__live sr-only' role='status' aria-live='polite'>
        {summary || (captchaFailed ? tf('captcha_failed') : '')}
      </p>

      {popUp.show && <Alert message={popUp.message} state={popUp.state} className='mb-4' onClose={() => setPopUp((previous) => ({ ...previous, show: false }))} />}

      <div className='mb-4'>
        <SsoProviders onAuthStart={handleSsoStart} onAuthSuccess={handleSsoSuccess} onAuthError={handleSsoError} trialContext={{ custom: trialConfigId, trialPageId }} />
      </div>

      <div className='np-form__fields'>
        {renderField('name', { id: 'np-name', type: 'text', autoComplete: 'name', label: tf('name'), placeholder: tf('name_placeholder') })}
        <div className='np-form__row np-form__row--2'>
          {renderField('email', { id: 'np-email', type: 'text', inputMode: 'email', autoComplete: 'email', label: tf('email'), placeholder: tf('email_placeholder') })}
          {renderField('repeatEmail', { id: 'np-email2', type: 'text', inputMode: 'email', autoComplete: 'email', label: tf('repeat_email'), placeholder: tf('repeat_email_placeholder') })}
        </div>
        {countryCodeUser === CountryCodeEnum.RUSSIAN && (
          <div className='np-field'>
            <label className='np-field__label' htmlFor='np-telegram'>
              {String(t('common:login.placeholder.telegram'))}
            </label>
            {/* Styled by `.np-field__control--phone` in v3b.module.css: no inline styles, they beat the field's focus state. */}
            <div className='np-field__control np-field__control--phone'>
              <PhoneInput localization={phoneLocalization} enableSearch searchNotFound={String(t('common:login.search_country_not_found'))} searchPlaceholder={String(t('common:login.search'))} inputProps={{ id: 'np-telegram', name: 'telegram', autoComplete: 'tel' }} inputClass='np-field__input' placeholder={String(t('common:login.placeholder.telegram'))} onChange={(value: string, _data: CountryData) => setValue('telegram', value, { shouldValidate: true, shouldDirty: true })} />
            </div>
            <span className='np-field__msg' />
          </div>
        )}
        <div className='np-form__row np-form__row--2'>
          {renderField('password', { id: 'np-pass', type: 'password', autoComplete: 'new-password', label: tf('password'), placeholder: tf('password_placeholder'), hint: tf('password_hint'), toggle: { show: tf('show_password'), hide: tf('hide_password') } })}
          {renderField('passwordRepeat', { id: 'np-pass2', type: 'password', autoComplete: 'new-password', label: tf('repeat_password'), placeholder: tf('repeat_password_placeholder'), toggle: { show: tf('show_repeat_password'), hide: tf('hide_repeat_password') } })}
        </div>
      </div>

      <div className='np-form__turnstile'>
        <div className='np-form__captcha' ref={captchaSlotRef}>
          <CaptchaProvider theme='dark' resetRef={captchaResetRef} onChange={handleCaptchaChange} onExpired={handleCaptchaExpired} onError={resetCaptcha} />
        </div>
        <div className='np-form__turnstile-ph' data-state={captchaState === 'mounted' ? 'ready' : captchaState} hidden={captchaState === 'mounted'}>
          <svg className='np-form__shield' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
            <path d='M12 3 4.5 6v5.3c0 4.6 3.1 8.7 7.5 9.7 4.4-1 7.5-5.1 7.5-9.7V6L12 3Z' />
            <path className='np-form__shield-check' d='m9.1 12.3 2 2 3.9-4.4' />
          </svg>
          <span className='np-form__turnstile-copy'>
            <span className='np-form__turnstile-text'>{captchaFailed ? tf('captcha_failed') : tf('captcha_title')}</span>
            <span className='np-form__turnstile-sub' hidden={captchaFailed}>
              {captchaState === 'loading' ? tf('captcha_loading') : tf('captcha_ready')}
            </span>
          </span>
          <span className='np-form__spinner' aria-hidden='true' />
          <button
            className='np-form__turnstile-retry'
            type='button'
            onClick={() => {
              setCaptchaState('loading');
              resetCaptcha();
            }}
          >
            {tf('captcha_retry')}
          </button>
        </div>
      </div>
      {errors.recaptcha && (
        <p className='np-field__msg is-error mt-2' role='alert'>
          {errors.recaptcha.message}
        </p>
      )}

      <div className={classNames('np-form__status np-form__status--top', summary && 'is-visible')} data-state={summary ? 'error' : undefined}>
        <div>
          {summary && (
            <>
              <ErrorIcon />
              <span>{summary}</span>
            </>
          )}
        </div>
      </div>

      <button className={classNames('btn btn-primary btn--block np-form__submit', isLoading && 'is-busy')} type='submit' id='click_obtenha_sete_dias_gratuitos' disabled={isDisabledButton || isLoading} aria-busy={isLoading ? 'true' : undefined}>
        <span className='np-form__submit-label'>{isLoading ? tf('submitting') : <Trans t={t} ns={NAMESPACE} i18nKey='form.submit' components={{ end: <SubmitEnd /> }} />}</span>
        <svg className='np-form__submit-spin' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' aria-hidden='true' focusable='false'>
          <path d='M12 3a9 9 0 1 1-6.4 2.6' opacity='.35' />
          <path d='M12 3a9 9 0 0 1 9 9' />
        </svg>
      </button>

      <div className='np-form__status'>
        <div />
      </div>

      <ul className='np-form__games' aria-label={tf('games_aria')}>
        {['Valorant', 'CS2', 'League of Legends', 'Fortnite', 'Apex Legends'].map((game) => (
          <li key={game}>{game}</li>
        ))}
      </ul>

      <p className='np-form__legal'>
        <Trans
          t={t}
          ns={NAMESPACE}
          i18nKey='form.legal'
          components={{
            terms: <NewTabLink href='/terms/terms-of-use' />,
            privacy: <NewTabLink href='/terms/privacy-policy' />,
          }}
        />
      </p>
    </form>
  );
}
